import React, { useState, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper pour obtenir le session ID
const getAdSessionId = () => {
  let sessionId = localStorage.getItem('ad_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('ad_session_id', sessionId);
  }
  return sessionId;
};

// Composant pour afficher un script AdSense / régie tierce
const AdSenseRenderer = ({ script, className = "" }) => {
  const containerRef = useRef(null);

  const needsIframe = /highperformanceformat\.com|effectivegatecpm\.com|atOptions\s*=\s*\{/.test(
    script || ''
  );
  const sizeMatch = {
    width: script?.match(/['"]width['"]\s*:\s*(\d+)/)?.[1],
    height: script?.match(/['"]height['"]\s*:\s*(\d+)/)?.[1]
  };
  const iframeWidth = sizeMatch.width ? Number(sizeMatch.width) : null;
  const iframeHeight = sizeMatch.height ? Number(sizeMatch.height) : null;
  const srcDoc = `
<!doctype html>
<html>
  <head><meta charset="utf-8"></head>
  <body style="margin:0;padding:0;overflow:hidden;background:transparent;">
    ${script || ''}
  </body>
</html>`;

  useEffect(() => {
    if (!script || !containerRef.current || needsIframe) return;
    
    // Nettoyer le conteneur
    containerRef.current.innerHTML = '';
    
    // Créer un conteneur pour le script
    const wrapper = document.createElement('div');
    wrapper.innerHTML = script;
    
    // Trouver et exécuter les scripts
    const scripts = wrapper.querySelectorAll('script');
    scripts.forEach(oldScript => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode?.replaceChild(newScript, oldScript);
    });
    
    // Ajouter le contenu au conteneur
    containerRef.current.appendChild(wrapper);
    
    // Essayer de pousser les pubs AdSense si la librairie est chargée
    try {
      if (window.adsbygoogle) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {
      console.warn('AdSense push error:', e);
    }
  }, [script, needsIframe]);

  if (!script) return null;

  if (needsIframe) {
    return (
      <iframe
        title="ad-frame"
        className={`adsense-container ${className}`}
        style={{
          width: iframeWidth ? `${iframeWidth}px` : '100%',
          height: iframeHeight ? `${iframeHeight}px` : '90px',
          border: '0',
          overflow: 'hidden',
          display: 'block'
        }}
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        srcDoc={srcDoc}
      />
    );
  }

  return (
    <div 
      ref={containerRef} 
      className={`adsense-container ${className}`}
      data-testid="adsense-container"
    />
  );
};

// Hook simplifié pour charger une publicité
const useAd = (position) => {
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAd = async () => {
      try {
        setLoading(true);
        const sessionId = getAdSessionId();
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        const positionParam = encodeURIComponent(position);
        const countryParam = encodeURIComponent('FR');
        const languageParam = encodeURIComponent('fr');
        const sessionParam = encodeURIComponent(sessionId);
        const response = await fetch(
          `${API_URL}/api/ads/serve/visual/${positionParam}/${countryParam}/${languageParam}/${sessionParam}`,
          { headers }
        );
        const result = await response.json();
        
        if (result.ad) {
          setAd(result.ad);
        }
      } catch (error) {
        console.error('Erreur chargement pub:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAd();
  }, [position]);

  const handleClick = async () => {
    if (ad?.click_url) {
      try {
        const sessionId = getAdSessionId();
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/api/ads/click/${ad.id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ session_id: sessionId })
        });
        window.open(ad.click_url, '_blank');
      } catch (error) {
        window.open(ad.click_url, '_blank');
      }
    }
  };

  return { ad, handleClick, loading };
};

// ==================== BANNIÈRES HAUT/BAS ====================

export const AdBanner = ({ position = 'banner_bottom', userIsPremium = false }) => {
  const { ad, handleClick } = useAd(position);

  if (userIsPremium) return null;
  if (!ad) return null;

  // Si c'est une pub AdSense avec un script
  if (ad.is_adsense && ad.adsense_script) {
    return (
      <div className={`w-full ${position === 'banner_top' ? 'mb-4' : 'mt-4'}`} data-testid={`ad-banner-${position}`}>
        <div className="bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] rounded-lg border border-yellow-500/30 py-3 px-4">
          <p className="text-[10px] text-yellow-500 uppercase tracking-wider font-bold mb-2">Sponsorisé</p>
          <AdSenseRenderer script={ad.adsense_script} className="w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${position === 'banner_top' ? 'mb-4' : 'mt-4'}`} data-testid={`ad-banner-${position}`}>
      <div className="bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] rounded-lg border border-yellow-500/30 py-3 px-4">
        {ad.image_url ? (
          <button
            type="button"
            onClick={handleClick}
            className="w-full flex justify-center"
            aria-label={ad.title || 'Publicité'}
          >
            <img
              src={ad.image_url}
              alt={ad.title || 'Publicité'}
              className="w-full max-w-[728px] h-auto object-contain"
            />
          </button>
        ) : (
          <div className="flex items-center gap-4 relative max-w-screen-xl mx-auto">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-yellow-500 uppercase tracking-wider font-bold">Sponsorisé</p>
              {ad.title && (
                <h4 
                  className="font-semibold text-white truncate cursor-pointer hover:underline text-sm"
                  onClick={handleClick}
                >
                  {ad.title}
                </h4>
              )}
              {ad.description && (
                <p className="text-xs text-white/60 truncate">{ad.description}</p>
              )}
            </div>
            
            {ad.click_url && (
              <button
                onClick={handleClick}
                className="px-4 py-1.5 bg-yellow-500 text-black rounded-full text-sm font-medium hover:bg-yellow-400 transition-colors flex-shrink-0"
              >
                En savoir plus
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== CARTE PUBLICITAIRE INLINE (pour grilles) ====================

const AdCardInlineContent = ({ ad, handleClick, className = "", variant = "square" }) => {
  const isLandscape = variant === "landscape";

  // Si c'est une pub AdSense avec un script
  if (ad.is_adsense && ad.adsense_script) {
    return (
      <div 
        className={`bg-gradient-to-b from-yellow-900/40 to-yellow-900/20 rounded-lg border border-yellow-500/40 p-3 ${isLandscape ? 'aspect-[16/10]' : ''} ${className}`}
        data-testid="ad-card-inline-adsense"
      >
        <div className="text-[10px] text-yellow-500 uppercase tracking-wider font-bold mb-2">Sponsorisé</div>
        <AdSenseRenderer script={ad.adsense_script} className={isLandscape ? "w-full h-full" : "w-full"} />
      </div>
    );
  }

  if (isLandscape) {
    return (
      <button
        onClick={handleClick}
        className={`group relative aspect-[16/10] overflow-hidden rounded-2xl border border-yellow-500/40 bg-gradient-to-br from-yellow-900/40 to-yellow-900/20 transition-all hover:from-yellow-900/60 hover:to-yellow-900/30 ${className}`}
        data-testid="ad-card-inline"
      >
        {ad.image_url ? (
          <img
            src={ad.image_url}
            alt={ad.title || 'Publicité'}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-600 to-orange-600 flex items-center justify-center text-4xl">
            📢
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>

        <div className="relative z-10 h-full p-3 flex flex-col justify-between">
          <div className="inline-flex items-center self-start px-2 py-0.5 bg-yellow-500 rounded text-[10px] text-black font-bold uppercase">
            Sponsorisé
          </div>

          <div className="flex items-end justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">{ad.title || 'Publicité'}</p>
              {ad.description && (
                <p className="text-xs text-white/70 line-clamp-2">{ad.description}</p>
              )}
            </div>
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-yellow-500 text-black shadow-lg opacity-0 group-hover:opacity-100 transition-all">
              <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      className={`group bg-gradient-to-b from-yellow-900/40 to-yellow-900/20 hover:from-yellow-900/60 hover:to-yellow-900/30 rounded-lg transition-all text-left flex flex-col border border-yellow-500/40 ${className}`}
      data-testid="ad-card-inline"
    >
      <div className="w-full aspect-square rounded-t-lg overflow-hidden relative">
        {ad.image_url ? (
          <img
            src={ad.image_url}
            alt={ad.title || 'Publicité'}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-yellow-600 to-orange-600 flex items-center justify-center text-4xl">
            📢
          </div>
        )}
        
        {/* Badge Sponsorisé */}
        <div className="absolute top-2 left-2 px-2 py-0.5 bg-yellow-500 rounded text-[10px] text-black font-bold uppercase">
          Sponsorisé
        </div>
        
        {/* Bouton CTA en bas à droite */}
        <div className="absolute bottom-2 right-2 w-12 h-12 rounded-full bg-yellow-500 flex items-center justify-center text-black shadow-lg opacity-0 group-hover:opacity-100 transition-all hover:scale-110">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </div>
      </div>
      <div className="p-3">
        <h3 className="font-bold text-sm truncate mb-1 text-yellow-100">{ad.title || 'Publicité'}</h3>
        <p className="text-xs text-white/60 truncate">{ad.description || 'Découvrir'}</p>
      </div>
    </button>
  );
};

export const AdCardInline = ({ userIsPremium = false, className = "", variant = "square" }) => {
  const { ad, handleClick } = useAd('card');

  if (userIsPremium || !ad) return null;

  return (
    <AdCardInlineContent
      ad={ad}
      handleClick={handleClick}
      className={className}
      variant={variant}
    />
  );
};

// Version "slot" : ne rend pas le wrapper si aucune pub disponible
export const AdCardInlineSlot = ({
  userIsPremium = false,
  className = "",
  variant = "square",
  wrapperClassName = "",
  wrapperStyle,
  innerClassName = ""
}) => {
  const { ad, handleClick } = useAd('card');

  if (userIsPremium || !ad) return null;

  const content = (
    <AdCardInlineContent
      ad={ad}
      handleClick={handleClick}
      className={className}
      variant={variant}
    />
  );

  if (wrapperClassName || wrapperStyle || innerClassName) {
    return (
      <div className={wrapperClassName} style={wrapperStyle}>
        {innerClassName ? <div className={innerClassName}>{content}</div> : content}
      </div>
    );
  }

  return content;
};

// Ancien nom pour compatibilité
export const AdCard = AdCardInline;

// ==================== PUB SIDEBAR GAUCHE ====================

export const AdSidebarLeft = ({ userIsPremium = false }) => {
  const { ad, handleClick } = useAd('sidebar_left');

  if (userIsPremium || !ad) return null;

  // Si c'est une pub AdSense avec un script
  if (ad.is_adsense && ad.adsense_script) {
    return (
      <div 
        className="px-2 py-2 rounded-lg bg-gradient-to-r from-yellow-900/40 to-orange-900/40 border border-yellow-500/40"
        data-testid="ad-sidebar-left-adsense"
      >
        <p className="text-[9px] text-yellow-500 uppercase tracking-wider font-bold mb-2">Sponsorisé</p>
        <AdSenseRenderer script={ad.adsense_script} className="w-full" />
      </div>
    );
  }

  return (
    <div 
      className="flex items-center gap-3 px-2 py-2 rounded-lg bg-gradient-to-r from-yellow-900/40 to-orange-900/40 hover:from-yellow-900/60 hover:to-orange-900/60 cursor-pointer transition-all border border-yellow-500/40"
      onClick={handleClick}
      data-testid="ad-sidebar-left"
    >
      {ad.image_url ? (
        <img
          src={ad.image_url}
          alt={ad.title || 'Publicité'}
          className="w-10 h-10 object-cover rounded flex-shrink-0"
        />
      ) : (
        <div className="w-10 h-10 bg-gradient-to-br from-yellow-600 to-orange-600 rounded flex items-center justify-center text-lg flex-shrink-0">
          📢
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[9px] text-yellow-500 uppercase tracking-wider font-bold">Sponsorisé</p>
        <p className="text-sm font-medium truncate">{ad.title || 'Publicité'}</p>
      </div>
    </div>
  );
};

// ==================== PUB SIDEBAR DROIT ====================

export const AdSidebarRight = ({ userIsPremium = false }) => {
  const { ad, handleClick } = useAd('sidebar_right');

  if (userIsPremium || !ad) return null;

  // Si c'est une pub AdSense avec un script
  if (ad.is_adsense && ad.adsense_script) {
    return (
      <div 
        className="p-3 rounded-lg bg-gradient-to-b from-yellow-900/30 to-transparent border border-yellow-500/40"
        data-testid="ad-sidebar-right-adsense"
      >
        <p className="text-[10px] text-yellow-500 uppercase tracking-wider mb-2 font-bold">Sponsorisé</p>
        <AdSenseRenderer script={ad.adsense_script} className="w-full" />
      </div>
    );
  }

  return (
    <div 
      className="p-3 rounded-lg bg-gradient-to-b from-yellow-900/30 to-transparent border border-yellow-500/40 cursor-pointer hover:bg-yellow-900/40 transition-all"
      onClick={handleClick}
      data-testid="ad-sidebar-right"
    >
      <p className="text-[10px] text-yellow-500 uppercase tracking-wider mb-2 font-bold">Sponsorisé</p>
      {ad.image_url && (
        <img
          src={ad.image_url}
          alt={ad.title || 'Publicité'}
          className="w-full h-28 object-cover rounded-lg mb-2"
        />
      )}
      <h4 className="font-semibold text-sm truncate">{ad.title || 'Publicité'}</h4>
      {ad.description && (
        <p className="text-xs text-white/60 line-clamp-2 mt-1">{ad.description}</p>
      )}
      {ad.click_url && (
        <button className="mt-2 w-full py-1.5 bg-yellow-500 text-black rounded-full text-xs font-medium hover:bg-yellow-400 transition-colors">
          En savoir plus
        </button>
      )}
    </div>
  );
};

// ==================== PUB PAGE (Artiste, Playlist, Likés) ====================

export const AdPageBanner = ({ userIsPremium = false }) => {
  const { ad, handleClick } = useAd('page_banner');

  if (userIsPremium || !ad) return null;

  // Si c'est une pub AdSense avec un script
  if (ad.is_adsense && ad.adsense_script) {
    return (
      <div 
        className="my-6 p-4 rounded-xl bg-gradient-to-r from-yellow-900/30 via-orange-900/20 to-yellow-900/30 border border-yellow-500/40"
        data-testid="ad-page-banner-adsense"
      >
        <p className="text-[10px] text-yellow-500 uppercase tracking-wider mb-3 font-bold">Sponsorisé</p>
        <AdSenseRenderer script={ad.adsense_script} className="w-full" />
      </div>
    );
  }

  return (
    <div 
      className="my-6 p-4 rounded-xl bg-gradient-to-r from-yellow-900/30 via-orange-900/20 to-yellow-900/30 border border-yellow-500/40 cursor-pointer hover:border-yellow-500/60 transition-all"
      onClick={handleClick}
      data-testid="ad-page-banner"
    >
      <div className="flex items-center gap-4">
        {ad.image_url && (
          <img
            src={ad.image_url}
            alt={ad.title || 'Publicité'}
            className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-yellow-500 uppercase tracking-wider mb-1 font-bold">Sponsorisé</p>
          <h3 className="text-lg font-bold truncate">{ad.title || 'Publicité'}</h3>
          {ad.description && (
            <p className="text-sm text-white/60 line-clamp-2 mt-1">{ad.description}</p>
          )}
        </div>
        {ad.click_url && (
          <button className="px-6 py-2 bg-yellow-500 text-black rounded-full font-medium hover:bg-yellow-400 transition-colors flex-shrink-0">
            Découvrir
          </button>
        )}
      </div>
    </div>
  );
};

// ==================== POPUP INTERSTITIEL (Modal plein écran) ====================

export const AdInterstitial = ({ isOpen, onClose, userIsPremium = false }) => {
  const { ad, handleClick, loading } = useAd('interstitial');
  const [countdown, setCountdown] = useState(5);
  const [canClose, setCanClose] = useState(false);

  useEffect(() => {
    if (isOpen && !userIsPremium && ad) {
      setCountdown(5);
      setCanClose(false);
      
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setCanClose(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isOpen, userIsPremium, ad]);

  // Ne pas afficher si premium, pas de pub, ou pas ouvert
  if (!isOpen || userIsPremium || loading) return null;
  if (!ad) {
    // Si pas de pub disponible, fermer automatiquement
    onClose?.();
    return null;
  }

  const handleAdClick = () => {
    handleClick();
  };

  const handleCloseClick = () => {
    if (canClose) {
      onClose?.();
    }
  };

  // Si c'est une pub AdSense avec un script
  const isAdsense = ad.is_adsense && ad.adsense_script;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center modal-text" data-testid="ad-interstitial">
      {/* Overlay sombre */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />
      
      {/* Contenu de la pub */}
      <div className="relative z-10 w-full max-w-lg mx-4 bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] rounded-2xl border border-yellow-500/50 overflow-hidden shadow-2xl">
        {/* Header avec compte à rebours */}
        <div className="flex items-center justify-between px-4 py-3 bg-yellow-500/10 border-b border-yellow-500/30">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
            <span className="text-xs text-yellow-500 font-bold uppercase tracking-wider">Publicité</span>
          </div>
          
          {canClose ? (
            <button
              onClick={handleCloseClick}
              className="flex items-center gap-2 px-3 py-1 bg-white/10 hover:bg-white/20 rounded-full text-sm transition-colors"
            >
              <span>Continuer</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1 bg-white/5 rounded-full text-sm text-white/60">
              <span>Passer dans</span>
              <span className="w-6 h-6 rounded-full bg-yellow-500 text-black font-bold flex items-center justify-center text-xs">
                {countdown}
              </span>
            </div>
          )}
        </div>

        {/* Contenu AdSense ou image normale */}
        {isAdsense ? (
          <div className="p-6">
            <AdSenseRenderer script={ad.adsense_script} className="w-full min-h-[250px]" />
          </div>
        ) : (
          <>
            {/* Image principale */}
            <div className="relative aspect-video cursor-pointer" onClick={handleAdClick}>
              {ad.image_url ? (
                <img
                  src={ad.image_url}
                  alt={ad.title || 'Publicité'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-yellow-600 to-orange-600 flex items-center justify-center">
                  <span className="text-8xl">📢</span>
                </div>
              )}
              
              {/* Overlay avec CTA */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex items-end p-6">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-white mb-2">{ad.title || 'Découvrez notre offre'}</h2>
                  {ad.description && (
                    <p className="text-white/80 text-sm">{ad.description}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer avec CTA */}
            <div className="p-4 flex items-center justify-between gap-4">
              <p className="text-xs text-white/40 flex-1">
                Cliquez pour en savoir plus sur cette offre exclusive
              </p>
              <button
                onClick={handleAdClick}
                className="px-6 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-full transition-colors flex items-center gap-2"
              >
                <span>En savoir plus</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== LECTEUR PUB AUDIO ====================

export const AudioAdPlayer = ({ ad, onComplete, onSkip }) => {
  const audioRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [canSkip, setCanSkip] = useState(false);

  useEffect(() => {
    if (audioRef.current && ad?.audio_url) {
      audioRef.current.play().catch(console.error);
    }
  }, [ad]);

  useEffect(() => {
    const timer = setTimeout(() => setCanSkip(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setProgress(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleClick = async () => {
    if (ad?.click_url) {
      try {
        const sessionId = getAdSessionId();
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/api/ads/click/${ad.id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ session_id: sessionId })
        });
        window.open(ad.click_url, '_blank');
      } catch (error) {
        window.open(ad.click_url, '_blank');
      }
    }
  };

  const handleEnded = async () => {
    try {
      const sessionId = getAdSessionId();
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/ads/complete/${ad.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ session_id: sessionId })
      });
    } catch (error) {
      console.error('Erreur complétion pub:', error);
    }
    onComplete?.();
  };

  if (!ad) return null;

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[70] bg-gradient-to-b from-[#181818] to-black border-t border-yellow-500/30">
      <audio
        ref={audioRef}
        src={ad.audio_url}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />
      
      <div className="absolute top-0 left-0 right-0 h-1 bg-white/10">
        <div
          className="h-full bg-yellow-500 transition-all"
          style={{ width: duration ? `${(progress / duration) * 100}%` : '0%' }}
        />
      </div>

      <div className="flex items-center justify-between px-4 py-3 max-w-screen-xl mx-auto">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-orange-500 rounded flex items-center justify-center flex-shrink-0">
            <span className="text-2xl">📢</span>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-yellow-500 uppercase tracking-wider font-semibold">Publicité</p>
            <h4 className="font-semibold text-white truncate">{ad.title || 'Annonce'}</h4>
            {ad.description && <p className="text-xs text-white/60 truncate">{ad.description}</p>}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button disabled className="w-8 h-8 flex items-center justify-center text-white/20 cursor-not-allowed">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
            </svg>
          </button>
          <div className="w-12 h-12 rounded-full bg-yellow-500 flex items-center justify-center">
            <svg className="w-5 h-5 text-black animate-pulse" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
          <button disabled className="w-8 h-8 flex items-center justify-center text-white/20 cursor-not-allowed">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
            </svg>
          </button>
        </div>

        <div className="flex items-center gap-4 flex-1 justify-end">
          <span className="text-sm text-white/60">
            {formatTime(progress)} / {formatTime(duration)}
          </span>
          
          {canSkip ? (
            <button
              onClick={onSkip || handleEnded}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-sm font-medium transition-colors"
            >
              Passer →
            </button>
          ) : (
            <span className="text-sm text-white/40 min-w-[100px] text-right">
              Passer dans {Math.max(0, 5 - Math.floor(progress))}s
            </span>
          )}
          
          {ad.click_url && (
            <button
              onClick={handleClick}
              className="px-4 py-2 bg-yellow-500 text-black rounded-full text-sm font-medium hover:bg-yellow-400 transition-colors"
            >
              En savoir plus
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Hook de géolocalisation (gardé pour compatibilité)
export const useGeoLocation = () => {
  const [geoData, setGeoData] = useState({ country: 'AUTO', language: 'en' });

  useEffect(() => {
    const getLocales = () => {
      if (Array.isArray(navigator.languages) && navigator.languages.length) {
        return navigator.languages;
      }
      if (navigator.language) return [navigator.language];
      return [];
    };

    const detect = () => {
      const locales = getLocales();
      const primary = locales[0] || '';
      const language = (primary.split(/[-_]/)[0] || 'en').toLowerCase();
      setGeoData({
        country: 'AUTO',
        language
      });
    };

    detect();
    window.addEventListener('languagechange', detect);
    return () => window.removeEventListener('languagechange', detect);
  }, []);

  return geoData;
};

export default { AdBanner, AdCard, AdCardInline, AdCardInlineSlot, AdSidebarLeft, AdSidebarRight, AdPageBanner, AdInterstitial, AudioAdPlayer, useGeoLocation };
